import * as React from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type { FieldArrayWithId } from 'react-hook-form';
import { compose } from 'redux';

import { Button } from '@acceligentllc/storybook';

import ColorSquare from 'af-components/ColorSquare';
import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import type { BillingCodeGroupRow, FormValues } from './BillingCodeGroupsArray';
import styles from './styles.module.scss';

interface OwnProps {
	show: boolean;
	close: () => void;
	groups: BillingCodeGroupRow[];
	onSave: (groups: BillingCodeGroupRow[]) => void;
}

type Props = OwnProps;

const SortGroupsModal: React.FC<Props> = (props: Props) => {
	const { show, close, groups, onSave } = props;

	const [localGroups, setLocalGroups] = React.useState<BillingCodeGroupRow[]>([]);
	const [initialOrderKey, setInitialOrderKey] = React.useState<string>('');

	React.useEffect(() => {
		if (show) {
			// IMPORTANT: do NOT mutate props
			const sorted = [...(groups ?? [])].sort((a, b) => {
				return (a.index ?? 0) - (b.index ?? 0);
			});

			setLocalGroups(sorted);
			setInitialOrderKey(sorted.map((group) => group.id).join('|'));
			return;
		}

		setLocalGroups([]);
		setInitialOrderKey('');
	}, [show, groups]);

	const reorder = React.useCallback((fromIndex: number, toIndex: number) => {
		setLocalGroups((prev) => {
			const next = [...prev];
			const [moved] = next.splice(fromIndex, 1);
			next.splice(toIndex, 0, moved);
			return next;
		});
	}, []);

	const onDragEnd = React.useCallback(({ source, destination }: DropResult) => {
		if (!destination) {
			return;
		}
		if (source.index === destination.index) {
			return;
		}
		reorder(source.index, destination.index);
	}, [reorder]);

	const hasChanges = React.useMemo(() => {
		if (!show) {
			return false;
		}
		return localGroups.map((g) => g.id).join('|') !== initialOrderKey;
	}, [show, localGroups, initialOrderKey]);

	const handleSave = React.useCallback(() => {
		const updated = localGroups.map((g, i) => ({ ...g, index: i }));
		onSave(updated);
		close();
	}, [localGroups, onSave, close]);

	const renderRow = React.useCallback((group: FieldArrayWithId<FormValues, 'billingCodeGroups', 'id'>, idx: number) => {
		const draggableId = `group-${group.id}`;

		return (
			<Draggable draggableId={draggableId} index={idx} key={draggableId}>
				{(provided) => (
					<div
						ref={provided.innerRef}
						{...provided.draggableProps}
						className={styles['sort-groups-modal__row']}
						style={provided.draggableProps.style}
					>
						<div className={styles.group} >

							<div className={styles['group-index']}>
								{idx + 1}
							</div>

							<div className={styles['group-drag-handle']} {...provided.dragHandleProps}>
								<span className="icon-drag_indicator" />
							</div>
							<div className={styles['group-name']}>
								{
									group.color && (
										<ColorSquare
											color={group.color}
										/>
									)
								}
								<div >
									{group.name}
								</div>
							</div>
						</div>
					</div>
				)}
			</Draggable>
		);
	}, []);

	const renderList = React.useCallback(() => {
		return (
			<Droppable direction="vertical" droppableId="billing-code-group-sort">
				{(droppableProvided) => (
					<div
						ref={droppableProvided.innerRef}
						{...droppableProvided.droppableProps}
						className="sort-groups-modal__list"
					>
						{localGroups.map(renderRow)}
						{droppableProvided.placeholder}
					</div>
				)}
			</Droppable>
		);
	}, [localGroups, renderRow]);

	return (
		<CustomModal
			className="sort-groups-modal"
			closeModal={close}
			modalStyle="info"
			showModal={show}
			size="md"
		>
			<CustomModal.Header closeModal={close} title="Edit Sort Order" />
			<CustomModal.Body>
				<div className="sort-groups-modal__help">
					The order defined below will be used for sorting columns in the Job Tracking Table.
					Drag the handles next to the locations to reorder them.
				</div>

				<DragDropContext onDragEnd={onDragEnd}>
					{renderList()}
				</DragDropContext>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button label="Cancel" onClick={close} style="secondary" />
				<SubmitButton disabled={!hasChanges} label="Save" onClick={handleSave} />
			</CustomModal.Footer>
		</CustomModal>
	);
};

const enhance = compose<React.ComponentType<OwnProps>>(React.memo);

export default enhance(SortGroupsModal);
